import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Instagram, ArrowUpRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import HoursTable from "../components/HoursTable";
import EditableText from "../components/EditableText";
import EditableI18n from "../components/EditableI18n";
import { translations } from "../i18n";

const T_EN = translations.en;
const T_FR = translations.fr;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const [form, setForm] = useState({ full_name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ full_name: "", email: "", message: "" });
    } catch {} finally { setSending(false); }
  };

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <p className="qw-overline mb-6">
          <EditableText kEn="contact_overline_en" kFr="contact_overline_fr" label="Contact overline"
            fallback={{ en: T_EN.contact.overline, fr: T_FR.contact.overline }} />
        </p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-foreground text-balance mb-8 max-w-3xl">
          <EditableText kEn="contact_title_en" kFr="contact_title_fr" label="Contact title"
            fallback={{ en: T_EN.contact.title, fr: T_FR.contact.title }} />
        </h1>
        <p className="text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          <EditableText kEn="contact_intro_en" kFr="contact_intro_fr" label="Contact intro"
            fallback={{ en: T_EN.contact.subtitle, fr: T_FR.contact.subtitle }} />
        </p>
      </section>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      <section className="px-6 md:px-12 lg:px-20 py-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-8">
          <ContactItem icon={Phone} label={<EditableI18n k="contact.phone" label="Phone label" />} value={settings.phone} href={`tel:${settings.phone_e164}`} testId="contact-phone" />
          <ContactItem icon={Mail} label={<EditableI18n k="contact.emailL" label="Email label" />} value={settings.email} href={`mailto:${settings.email}`} testId="contact-email" />
          <ContactItem icon={MapPin} label={<EditableI18n k="contact.location" label="Location label" />} value={`${settings.location_dakar} · ${settings.location_london}`} testId="contact-location" />
          <ContactItem icon={Instagram} label="Instagram" value={settings.instagram_handle} href={settings.instagram_url} testId="contact-instagram" />

          <div className="border-t border-border pt-8">
            <p className="qw-overline mb-4"><EditableI18n k="hours.title" label="Hours title" /></p>
            <HoursTable />
          </div>
        </div>

        <div className="lg:col-span-7">
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-10"><EditableI18n k="contact.sendNote" label="Form heading" /></h2>
          {sent ? (
            <p data-testid="contact-success" className="text-primary text-base border-l-2 border-primary pl-6 py-2">{t.contact.sent}</p>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8 max-w-xl">
              <div>
                <label className="qw-overline block mb-2"><EditableI18n k="contact.yourName" label="Name label" /></label>
                <input data-testid="contact-name-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="qw-input" />
              </div>
              <div>
                <label className="qw-overline block mb-2"><EditableI18n k="contact.yourEmail" label="Email label" /></label>
                <input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="qw-input" />
              </div>
              <div>
                <label className="qw-overline block mb-2"><EditableI18n k="contact.yourMessage" label="Message label" /></label>
                <textarea data-testid="contact-message-input" rows="4" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="qw-input resize-none" />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="qw-btn qw-btn-primary disabled:opacity-60">
                {sending ? t.contact.sending : <EditableI18n k="contact.send" label="Send button" />}
                {!sending && <ArrowUpRight size={16} strokeWidth={1.2} />}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function ContactItem({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <div className="flex items-start gap-5">
      <div className="h-10 w-10 border border-primary/60 flex items-center justify-center flex-shrink-0">
        <Icon size={16} strokeWidth={1.2} className="text-primary" />
      </div>
      <div className="flex-1">
        <p className="qw-overline mb-1">{label}</p>
        <p className="text-base text-foreground">{value}</p>
      </div>
    </div>
  );
  if (href) {
    return (
      <a data-testid={testId} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block group hover:text-primary transition-colors">
        {inner}
      </a>
    );
  }
  return <div data-testid={testId}>{inner}</div>;
}
